import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useParams, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, CircularProgress, Box, Typography } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SwipeableView } from './components/SwipeableView';
import { SiteProxy } from './components/SiteProxy';
import { AdminLayout } from './components/admin/AdminLayout';
import { Dashboard } from './components/admin/Dashboard';
import { SiteList } from './components/admin/SiteList';
import { VisualEditor } from './components/admin/VisualEditor';
import { Analytics } from './components/admin/Analytics';
import { UserList } from './components/admin/UserList';
import { UserEditor } from './components/admin/UserEditor';
import { Login } from './components/admin/Login';
import { NotFound } from './components/admin/NotFound';
import { AdminIndexRedirect } from './components/admin/AdminIndexRedirect';
import { Page, LandingPage } from './types';
import { DomainService } from './services/domainService';
import { SiteService } from './services/siteService';
import './App.css';
import './styles/SwipeableView.css';

// Material-UIテーマ設定
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
});

// qwipe.jpドメインのみ/admin配下にアクセス可能にするコンポーネント
const AdminRouteGuard: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [isAllowedDomain, setIsAllowedDomain] = useState(false);

  useEffect(() => {
    const checkDomain = async () => {
      const hostname = window.location.hostname;
      console.log('AdminRouteGuard: チェック中のホスト名:', hostname);
      
      // 開発環境やFirebase公式ドメインは常に許可
      const alwaysAllowedHosts = [
        'localhost',
        '127.0.0.1',
        'qwipesystem.web.app',
        'qwipesystem.firebaseapp.com'
      ];
      
      // qwipe.jpドメインは常に許可
      const allowedAdminDomains = [
        'qwipe.jp',
        'www.qwipe.jp',
        'preview.qwipe.jp',
        'www.preview.qwipe.jp'
      ];
      
      if (alwaysAllowedHosts.includes(hostname)) {
        console.log('AdminRouteGuard: 常に許可されるホストです');
        setIsAllowedDomain(true);
        setLoading(false);
        return;
      }
      
      // qwipe.jpドメインの場合は許可（最優先）
      if (allowedAdminDomains.includes(hostname)) {
        console.log('AdminRouteGuard: qwipe.jpドメインとして許可');
        setIsAllowedDomain(true);
        setLoading(false);
        return;
      }
      
      try {
        // Firestoreのdomainsコレクションにこのドメインが存在するか確認
        const domainData = await DomainService.getDomainByName(hostname);
        console.log('AdminRouteGuard: Firestore確認結果:', domainData);
        
        // Cloudflare Zone Managementで管理されているカスタムドメインの場合
        if (domainData) {
          // qwipe.jp以外のカスタムドメインはアクセス不可
          console.log('AdminRouteGuard: カスタムドメインとしてブロック');
          setIsAllowedDomain(false);
        } else {
          // Firestoreに存在しない = Firebase Hostingで直接設定されたドメイン（許可）
          console.log('AdminRouteGuard: Firebase Hostingドメインとして許可');
          setIsAllowedDomain(true);
        }
      } catch (error) {
        console.error('AdminRouteGuard: ドメイン確認エラー:', error);
        // エラー時は安全側に倒してアクセス不可とする
        setIsAllowedDomain(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkDomain();
  }, []);

  // ドメイン確認中
  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // 許可されていないドメインの場合はルートにリダイレクト
  if (!isAllowedDomain) {
    return <Navigate to="/" replace />;
  }
  
  // childrenがある場合はそれをレンダリング、なければOutletをレンダリング
  return children ? <>{children}</> : <Outlet />;
};

// 認証が必要なルートを保護するコンポーネント
const ProtectedRoute: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  // 認証状態の確認中
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        fontSize: '18px'
      }}>
        認証状態を確認中...
      </div>
    );
  }
  
  // 認証されていない場合はログイン画面にリダイレクト
  if (!currentUser) {
    return <Navigate to="/admin/login" />;
  }
  
  // childrenがある場合はそれをレンダリング、なければそのまま（AdminLayoutを使用）
  return <>{children}</>;
};

const LoginRouteGuard: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return null;
  }
  
  if (currentUser) {
    const redirectPath = currentUser.role === 'viewer' ? '/admin/sites' : '/admin/dashboard';
    return <Navigate to={redirectPath} replace />;
  }
  
  return <>{children}</>;
};

// 閲覧者用のルートガード（閲覧者はサイト一覧と分析ページのみアクセス可能）
const ViewerRouteGuard: React.FC<{ children?: React.ReactNode; allowedPaths?: string[] }> = ({ children, allowedPaths = [] }) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        fontSize: '18px'
      }}>
        認証状態を確認中...
      </div>
    );
  }
  
  // 閲覧者以外はそのまま通過
  if (!currentUser || currentUser.role !== 'viewer') {
    return <>{children}</>;
  }
  
  // 閲覧者の場合、許可されたパスのみアクセス可能
  const isAllowed = allowedPaths.some(path => {
    const pathPattern = path.replace(/:[\w]+/g, '[^/]+');
    const regex = new RegExp(`^${pathPattern}$`);
    return regex.test(location.pathname);
  });
  
  if (!isAllowed) {
    return <NotFound />;
  }
  
  return <>{children}</>;
};

// FlutterアプリのLP構造に基づいたページデータ
const qwipeLpPages: Page[] = [
  {
    id: '1',
    pageId: '01',
    row: 1,
    column: 'A',
    type: 'vertical',
    isHorizontal: false,
    hasVideo: true,
    videoUrl: 'https://zaoric.com/images/zaoric.mp4',
    backgroundImage: '/assets/images/01.webp'
  },
  {
    id: '2',
    pageId: '02',
    row: 2,
    column: 'A',
    type: 'vertical',
    isHorizontal: false,
    backgroundImage: '/assets/images/02.webp'
  },
  {
    id: '3',
    pageId: '03',
    row: 3,
    column: 'A',
    type: 'vertical',
    isHorizontal: false,
    backgroundImage: '/assets/images/03.webp'
  },
  {
    id: '4',
    pageId: '04',
    row: 4,
    column: 'A',
    type: 'vertical',
    isHorizontal: false,
    backgroundImage: '/assets/images/04.webp'
  },
  {
    id: '5',
    pageId: '05',
    row: 5,
    column: 'A',
    type: 'vertical',
    isHorizontal: false,
    backgroundImage: '/assets/images/05.webp'
  },
  {
    id: '6',
    pageId: '06',
    row: 6,
    column: 'A',
    type: 'horizontal',
    isHorizontal: true,
    subPageIds: ['06_1', '06_2', '06_3'],
    backgroundImage: '/assets/images/06.webp'
  },
  {
    id: '7',
    pageId: '07',
    row: 7,
    column: 'A',
    type: 'vertical',
    isHorizontal: false,
    backgroundImage: '/assets/images/07.webp'
  },
  {
    id: '8',
    pageId: '08',
    row: 8,
    column: 'A',
    type: 'vertical',
    isHorizontal: false,
    backgroundImage: '/assets/images/08.webp'
  },
  {
    id: '9',
    pageId: '09',
    row: 9,
    column: 'A',
    type: 'horizontal',
    isHorizontal: true,
    subPageIds: ['09_1', '09_2'],
    backgroundImage: '/assets/images/09.webp'
  },
  {
    id: '10',
    pageId: '10',
    row: 10,
    column: 'A',
    type: 'vertical',
    isHorizontal: false,
    backgroundImage: '/assets/images/10.webp'
  },
  {
    id: '11',
    pageId: '11',
    row: 11,
    column: 'A',
    type: 'vertical',
    isHorizontal: false,
    backgroundImage: '/assets/images/11.webp'
  },
  {
    id: '12',
    pageId: '12',
    row: 12,
    column: 'A',
    type: 'vertical',
    isHorizontal: false,
    backgroundImage: '/assets/images/12.webp'
  }
];

// モックサイトデータ（真っ白なページ）
const mockSites = [
  {
    id: 'mock-site-1',
    title: 'Qwipe LP',
    description: 'Qwipeのランディングページ',
    pages: [], // ページを空にして真っ白にする
    status: 'published' as const,
    publishedUrl: 'https://qwipe.example.com',
    createdAt: new Date(),
    updatedAt: new Date()
  }
];


// カスタムドメイン検出コンポーネント
const CustomDomainRouter: React.FC = () => {
  const { siteId: urlSiteId } = useParams<{ siteId?: string }>();
  const [loading, setLoading] = useState(true);
  const [site, setSite] = useState<LandingPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [imagesPreloaded, setImagesPreloaded] = useState(false);
  const [swipeableViewReady, setSwipeableViewReady] = useState(false);

  // SwipeableViewの準備完了コールバックをuseCallbackでラップ
  const handleSwipeableViewReady = useCallback(() => {
    console.warn('SwipeableView準備完了');
    setSwipeableViewReady(true);
  }, []);

  // サイトが設定されたらすぐに表示を開始（画像読み込み完了を待たない）
  useEffect(() => {
    if (site && site.pages && site.pages.length > 0) {
      // すぐに準備完了として表示を開始
      setImagesPreloaded(true);
      setSwipeableViewReady(true);
    } else if (site && (!site.pages || site.pages.length === 0)) {
      // ページがない場合はすぐに完了とする
      setImagesPreloaded(true);
      setSwipeableViewReady(true);
    }
  }, [site]);

  // サイト設定のファビコンを <head> に反映（先頭に追加してブラウザに優先させる）
  useEffect(() => {
    const faviconUrl = site?.settings?.favicon;
    const id = 'qwipe-dynamic-favicon';
    let link = document.getElementById(id) as HTMLLinkElement | null;
    if (faviconUrl) {
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        link.id = id;
        const lower = faviconUrl.toLowerCase();
        if (lower.includes('.png')) link.type = 'image/png';
        else if (lower.includes('.webp') || lower.includes('webp')) link.type = 'image/webp';
        else if (lower.includes('.svg')) link.type = 'image/svg+xml';
        else link.type = 'image/x-icon';
        document.head.insertBefore(link, document.head.firstChild);
      }
      link.href = faviconUrl;
    }
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, [site?.id, site?.settings?.favicon]);

  useEffect(() => {
    const detectCustomDomain = async () => {
      try {
        const hostname = window.location.hostname;
        const pathname = window.location.pathname;
        
        // ローカル開発環境でのプレビューモード判定（パスにサイトIDがあるか確認）
        // http://localhost:3000/サイトID の形式でアクセスされた場合
        const pathSegments = pathname.split('/').filter(Boolean);
        const isLocalPreview = (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('qwipesystem.web.app') || hostname.includes('qwipesystem.firebaseapp.com')) 
          && pathSegments.length > 0;
        
        // preview.qwipe.jpの場合はプレビューモード
        if (hostname === 'preview.qwipe.jp' || hostname === 'www.preview.qwipe.jp' || isLocalPreview) {
          setIsPreviewMode(true);
          
          let siteData: LandingPage | null = null;
          let siteIdToFetch: string | null = null;
          
          // URLパスからサイトIDを取得
          if (urlSiteId) {
            siteIdToFetch = urlSiteId;
          } else if (pathSegments.length > 0) {
            siteIdToFetch = pathSegments[0];
          }
          
          if (siteIdToFetch) {
            console.log('プレビューモード: サイトID取得:', siteIdToFetch);
            try {
              siteData = await SiteService.getSite(siteIdToFetch);
              
              if (siteData) {
                console.log('プレビューサイトデータ取得成功:', siteData.title);
                console.log('プレビューサイトデータ - pages:', siteData.pages);
                console.log('プレビューサイトデータ - pagesの長さ:', siteData.pages?.length);
                console.log('プレビューサイトデータ - pagesが配列か:', Array.isArray(siteData.pages));
                
                // pagesが空または不正な形式の場合の警告
                if (!siteData.pages || !Array.isArray(siteData.pages) || siteData.pages.length === 0) {
                  console.warn('プレビューモード: 警告 - pagesが空または不正な形式です:', {
                    siteId: siteIdToFetch,
                    pages: siteData.pages,
                    pagesType: typeof siteData.pages,
                    isArray: Array.isArray(siteData.pages),
                    pagesLength: siteData.pages?.length
                  });
                }
                
                setSite(siteData);
                setLoading(false);
              } else {
                console.error('プレビューモード: サイトが見つかりません:', siteIdToFetch);
                setError('サイトが見つかりません');
                setImagesPreloaded(true);
                setLoading(false);
              }
            } catch (err) {
              console.error('プレビューモード: サイト取得エラー:', err);
              setError('サイトの取得に失敗しました');
              setImagesPreloaded(true);
              setLoading(false);
            }
          } else {
            console.error('プレビューモード: サイトIDが指定されていません');
            setError('サイトIDが指定されていません。URL形式: localhost:3002/サイトID または preview.qwipe.jp/サイトID');
            setImagesPreloaded(true);
            setLoading(false);
          }
          
          return;
        }
        
        // ローカル開発環境またはデフォルトドメインの場合は、モックサイトを表示
        if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('qwipesystem.web.app') || hostname.includes('qwipesystem.firebaseapp.com')) {
          setSite(mockSites[0] as any);
          setLoading(false);
          return;
        }

        // qwipe.jpの場合は特別処理（本サービス用のデフォルトコンテンツ）
        if (hostname === 'qwipe.jp' || hostname === 'www.qwipe.jp') {
          console.log('qwipe.jp検出: Firestoreから設定を取得');
          try {
            // パスベースのルーティングに対応
            const domainData = await DomainService.getDomainByNameAndPath(hostname, pathname);
            
            if (domainData && domainData.siteId) {
              console.log('qwipe.jp: サイトID取得:', domainData.siteId, 'basePath:', domainData.domainConfig.basePath);
              const siteData = await SiteService.getSite(domainData.siteId);
              
              if (siteData) {
                console.log('qwipe.jp: サイトデータ取得成功:', siteData.title);
                // 実際のステータスを計算
                const actualStatus = await SiteService.getActualStatus(siteData);
                console.log('qwipe.jp: 実際のステータス:', actualStatus, '元のステータス:', siteData.status);
                
                // ステータスが公開以外の場合は404を表示
                if (actualStatus !== 'published') {
                  console.log('qwipe.jp: サイトは公開されていません。404を表示します。');
                  setError('404 - ページが見つかりません');
                  setImagesPreloaded(true);
                  setLoading(false);
                  return;
                }
                
                setSite(siteData);
              } else {
                // サイトが見つからない場合はエラー
                setError('qwipe.jpに紐づくサイトが見つかりません。管理画面で設定してください。');
                setImagesPreloaded(true);
              }
            } else {
              // ドメイン設定が見つからない場合はエラー
              setError('qwipe.jpのドメイン設定が見つかりません。管理画面で設定してください。');
              setImagesPreloaded(true);
            }
          } catch (err) {
            console.error('qwipe.jp: ドメイン設定取得エラー:', err);
            setError('qwipe.jpの設定を読み込めませんでした。しばらく待ってから再度お試しください。');
            setImagesPreloaded(true);
          }
          setLoading(false);
          return;
        }

        // その他のカスタムドメインの場合、Firestoreから取得（パスベースのルーティングに対応）
        console.log('カスタムドメイン検出:', hostname, 'pathname:', pathname);
        const domainData = await DomainService.getDomainByNameAndPath(hostname, pathname);
        
        if (domainData && domainData.siteId) {
          console.log('サイトID取得:', domainData.siteId, 'basePath:', domainData.domainConfig.basePath);
          const siteData = await SiteService.getSite(domainData.siteId);
          
          if (siteData) {
            console.log('サイトデータ取得成功:', siteData.title);
            // 実際のステータスを計算
            const actualStatus = await SiteService.getActualStatus(siteData);
            console.log('実際のステータス:', actualStatus, '元のステータス:', siteData.status);
            
            // ステータスが公開以外の場合は404を表示
            if (actualStatus !== 'published') {
              console.log('サイトは公開されていません。404を表示します。');
              setError('404 - ページが見つかりません');
              setImagesPreloaded(true);
              setLoading(false);
              return;
            }
            
            setSite(siteData);
          } else {
            setError('サイトが見つかりません');
            setImagesPreloaded(true);
          }
        } else {
          // カスタムドメインの場合は、ドメイン設定が見つからない場合はエラー
          // デフォルトサイトを表示しない（ちらつき防止）
          console.log('ドメイン設定が見つかりません。');
          setError('このドメインに紐づくサイトが存在しません。');
          setImagesPreloaded(true);
        }
      } catch (err) {
        console.error('カスタムドメイン検出エラー:', err);
        setError('サイトの読み込みに失敗しました');
        setImagesPreloaded(true);
      } finally {
        setLoading(false);
      }
    };
    
    detectCustomDomain();
  }, [urlSiteId]);

  // プレビューモードの場合は、siteが設定されるまでローディングを継続
  if (loading || (isPreviewMode && !site && !error)) {
    return (
      <Box 
        sx={{
          width: '100vw',
          height: '100vh',
          backgroundColor: '#ffffff', // 白背景でデフォルト背景を隠す
        }}
      />
    );
  }

  if (error) {
    // 404エラーの場合は404ページを表示
    if (error === '404 - ページが見つかりません') {
      return (
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="100vh"
          flexDirection="column"
          gap={2}
          sx={{ backgroundColor: '#ffffff' }}
        >
          <Typography variant="h1" sx={{ fontSize: '6rem', fontWeight: 'bold', color: '#666' }}>
            404
          </Typography>
          <Typography variant="h5" color="textSecondary">
            ページが見つかりません
          </Typography>
          <Typography variant="body1" color="textSecondary">
            お探しのページは存在しないか、公開されていません。
          </Typography>
        </Box>
      );
    }
    
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        flexDirection="column"
        gap={2}
      >
        <Typography variant="h4" color="error">
          エラー
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!site || !site.pages || site.pages.length === 0) {
    // モックサイトの場合は真っ白な画面を表示
    if (site && site.id === 'mock-site-1') {
      return (
        <Box 
          sx={{
            width: '100vw',
            height: '100vh',
            backgroundColor: '#ffffff',
          }}
        />
      );
    }
    
    // プレビューモードでpagesが空の場合は、真っ白な画面を表示（データの問題の可能性）
    if (isPreviewMode && site) {
      console.warn('プレビューモード: pagesが空です。真っ白な画面を表示します:', {
        siteId: site.id,
        siteTitle: site.title,
        pages: site.pages,
        pagesLength: site.pages?.length
      });
      return (
        <Box 
          sx={{
            width: '100vw',
            height: '100vh',
            backgroundColor: '#ffffff',
          }}
        />
      );
    }
    
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        flexDirection="column"
        gap={2}
      >
        <Typography variant="h4" color="textSecondary">
          サイトが見つかりません
        </Typography>
        <Typography variant="body1" color="textSecondary">
          このドメインに紐づくサイトが存在しないか、公開されていません。
        </Typography>
      </Box>
    );
  }

  // 画像の事前読み込みが完了していない場合はローディングを継続
  // 背景画像も表示しないように背景色を白に設定
  if (!imagesPreloaded) {
    return (
      <Box 
        sx={{
          width: '100vw',
          height: '100vh',
          backgroundColor: '#ffffff', // 白背景でデフォルト背景を隠す
        }}
      />
    );
  }

  // imagesPreloadedがtrueになったら、SwipeableViewをレンダリングする
  // swipeableViewReadyはSwipeableViewのonReadyで設定される
  // swipeableViewReadyがfalseの間は、完全に白背景で隠す
  return (
    <div 
      className="App"
      style={{
        backgroundColor: '#ffffff', // 常に白背景（SwipeableViewが自身の背景を設定する）
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        minHeight: '100vh',
        position: 'relative'
      }}
    >
      {!swipeableViewReady && (
        <Box 
          sx={{
            backgroundColor: '#ffffff',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
          }}
        />
      )}
      <SwipeableView 
        pages={site.pages}
        site={site}
        isPreviewMode={isPreviewMode}
        onReady={handleSwipeableViewReady}
        onPageChange={(pageIndex: number) => {
          console.log('Current page:', pageIndex);
        }}
      />
    </div>
  );
};

// /test ルート - test.htmlを表示
const TestPage: React.FC = () => {
  return (
    <iframe
      src="/test.html"
      style={{
        width: '100%',
        height: '100vh',
        border: 'none',
        margin: 0,
        padding: 0,
        display: 'block'
      }}
      title="Qwipe埋め込みテスト"
    />
  );
};

function App() {
  const handlePageChange = (pageIndex: number) => {
    console.log('Current page:', pageIndex);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            {/* /test ルート - 埋め込みコンテンツを直接表示 */}
            <Route 
              path="/test" 
              element={<TestPage />} 
            />
            {/* プレビュールート - preview.qwipe.jp対応（先に定義することで優先される） */}
            <Route 
              path="/:siteId" 
              element={<CustomDomainRouter />} 
            />
            {/* パブリックルート - カスタムドメイン対応 */}
            <Route 
              path="/" 
              element={<CustomDomainRouter />} 
            />
            
            {/* すべての管理画面ルートをAdminRouteGuardでラップ（カスタムドメインからアクセス不可） */}
            <Route 
              path="/admin" 
              element={<AdminRouteGuard />}
            >
              {/* 管理画面ログイン */}
              <Route 
                path="login" 
                element={
                  <LoginRouteGuard>
                    <Login />
                  </LoginRouteGuard>
                } 
              />
              
              {/* 管理画面内のプレビュー（認証不要） */}
              <Route path="sites/:siteId/preview" element={<SiteProxy />} />
              
              {/* 保護された管理画面ルート */}
              <Route
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminIndexRedirect />} />
                <Route 
                  path="dashboard" 
                  element={
                    <ViewerRouteGuard allowedPaths={['/admin/sites']}>
                      <Dashboard />
                    </ViewerRouteGuard>
                  } 
                />
                <Route 
                  path="sites" 
                  element={
                    <ViewerRouteGuard allowedPaths={['/admin/sites', '/admin/sites/:siteId/analytics']}>
                      <SiteList />
                    </ViewerRouteGuard>
                  } 
                />
                <Route 
                  path="sites/create" 
                  element={
                    <ViewerRouteGuard allowedPaths={[]}>
                      <VisualEditor />
                    </ViewerRouteGuard>
                  } 
                />
                <Route 
                  path="sites/:siteId/edit" 
                  element={
                    <ViewerRouteGuard allowedPaths={[]}>
                      <VisualEditor />
                    </ViewerRouteGuard>
                  } 
                />
                <Route 
                  path="sites/:siteId/analytics" 
                  element={
                    <ViewerRouteGuard allowedPaths={['/admin/sites', '/admin/sites/:siteId/analytics']}>
                      <Analytics />
                    </ViewerRouteGuard>
                  } 
                />
                <Route 
                  path="users" 
                  element={
                    <ViewerRouteGuard allowedPaths={[]}>
                      <UserList />
                    </ViewerRouteGuard>
                  } 
                />
                <Route 
                  path="users/create" 
                  element={
                    <ViewerRouteGuard allowedPaths={[]}>
                      <UserEditor />
                    </ViewerRouteGuard>
                  } 
                />
                <Route 
                  path="users/:userId/edit" 
                  element={
                    <ViewerRouteGuard allowedPaths={[]}>
                      <UserEditor />
                    </ViewerRouteGuard>
                  } 
                />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Route>
            
            {/* レガシールート（カスタムドメインからアクセス不可） */}
            <Route 
              path="/dashboard" 
              element={
                <AdminRouteGuard>
                  <Navigate to="/admin/dashboard" />
                </AdminRouteGuard>
              } 
            />
            <Route 
              path="/editor" 
              element={
                <AdminRouteGuard>
                  <Navigate to="/admin/sites" />
                </AdminRouteGuard>
              } 
            />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
